/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";
import AppSettings from "./components/App/AppSettings";
import * as CONNECTION from './components/App/Constants/Connection';
import axios from "axios";

async function getAppSettings(): Promise<AppSettings>{
    return (
        await axios.get(
            CONNECTION.CONFIGURATION_FILE_PATH
        ).then(response => {
            if (response.status === 200) {
                return response!.data;
            }
        })
    );
}
const appSettings = await getAppSettings();

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {

    names: {
        signUpSignIn: appSettings.policyName,
        forgotPassword: appSettings.passwordResetPolicyName,
        passwordRegistration: appSettings.passwordRegistrationPolicyName,
    },
    authorities: {
        signUpSignIn: {
            authority: appSettings.msLogin.concat(appSettings.b2cDomain, "/", appSettings.policyName),
        },
        forgotPassword: {
            authority: appSettings.msLogin.concat(appSettings.b2cDomain, "/", appSettings.passwordResetPolicyName),
        },
        passwordRegistration: {
            authority: appSettings.msLogin.concat(appSettings.b2cDomain, "/", appSettings.passwordRegistrationPolicyName),
            scopes: ['openid'],
            response_type: 'id_token',
            prompt: 'login',
            extraQueryParameters: { 'id_token_hint': ''}
        },
    },
    authorityDomain: appSettings.authorityDomain,
}

/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */
export const msalConfig = {

    auth: {
        clientId: appSettings.clientID,
        authority: appSettings.msLogin.concat(appSettings.b2cDomain, "/", appSettings.policyName),
        knownAuthorities: [appSettings.authorityDomain],
        redirectUri: appSettings.redirectUri,
        postLogoutRedirectUri: appSettings.postLogoutRedirectUri,
        navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: 'localStorage', 
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback: (level: any, message: any, containsPii: any) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }
            }
        }
    }
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    //scopes: [...protectedResources.apiTodoList.scopes.read, ...protectedResources.apiTodoList.scopes.write],
    scopes: [appSettings.b2cScope]
};